/**
 * ExportCoverPage
 *
 * Submission-ready cover page for the PDF export, rendered as page 1 with no
 * running header/footer. Mirrors the Word cover page (BeautifulDocxExporter) and
 * the SerVet_Export_Reference visual target: wordmark + tagline, navy/red rule
 * pair, eyebrow, large navy title, issuing-office subtitle, identity meta block,
 * and a two-column Submitted To / Submitted By block.
 *
 * Styling is intentionally inline + light-only (no `dark:` classes, no bw-*
 * tokens) so the cover always renders identically regardless of app theme.
 */

import React from 'react';
import type { CoverPageModel } from '../../lib/export/coverPageModel';
import { TO_BE_COMPLETED } from '../../lib/export/companyIdentity';

const NAVY = '#1F4E96';
const RED = '#C8102E';
const INK = '#1A1A1A';
const MUTED = '#5A6472';
const SLATE = '#33415C';
const BORDER = '#C9D3E0';

export function ExportCoverPage({ model }: { model: CoverPageModel }) {
  const { identity } = model;

  return (
    <div
      className="export-cover-page"
      style={{
        fontFamily: '"Times New Roman", Georgia, serif',
        color: INK,
        background: '#ffffff',
        breakAfter: 'page',
        pageBreakAfter: 'always',
        minHeight: '9in',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Wordmark / logo */}
      <div style={{ marginBottom: '4px' }}>
        {identity.logoUrl ? (
          <img
            src={identity.logoUrl}
            alt={model.wordmark}
            onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
            style={{ maxHeight: '84px', maxWidth: '3.2in', objectFit: 'contain', display: 'block' }}
          />
        ) : (
          <div style={{ fontSize: '30px', fontWeight: 700, color: NAVY, letterSpacing: '0.01em' }}>
            {model.wordmark || 'Offeror'}
          </div>
        )}
        {model.tagline && (
          <div
            style={{
              fontSize: '10px',
              fontWeight: 700,
              color: RED,
              letterSpacing: '0.22em',
              textTransform: 'uppercase',
              marginTop: '4px',
            }}
          >
            {model.tagline}
          </div>
        )}
      </div>

      {/* Navy + red rule pair */}
      <div style={{ borderTop: `2.5px solid ${NAVY}`, marginTop: '10px' }} />
      <div style={{ borderTop: `1.5px solid ${RED}`, width: '40%', marginTop: '2px' }} />

      {/* Eyebrow */}
      <div
        style={{
          fontSize: '11px',
          fontWeight: 700,
          color: MUTED,
          letterSpacing: '0.18em',
          textTransform: 'uppercase',
          marginTop: '64px',
        }}
      >
        {model.eyebrow}
      </div>

      {/* Title */}
      <h1
        style={{
          fontSize: '30px',
          lineHeight: 1.15,
          fontWeight: 700,
          color: NAVY,
          margin: '10px 0 6px',
        }}
      >
        {model.title}
      </h1>

      {/* Subtitle (issuing office) */}
      {model.subtitle && (
        <div style={{ fontSize: '15px', color: SLATE, fontWeight: 600 }}>{model.subtitle}</div>
      )}

      {/* Volume label */}
      {model.volumeLabel && (
        <div style={{ fontSize: '13px', color: MUTED, marginTop: '10px' }}>
          <span style={{ fontWeight: 700, color: NAVY }}>Volume:&nbsp;</span>
          {model.volumeLabel}
        </div>
      )}

      {/* Identity meta block */}
      <table style={{ marginTop: '28px', borderCollapse: 'collapse', width: 'auto' }}>
        <tbody>
          {model.meta.map((row, i) => (
            <tr key={i}>
              <td
                style={{
                  fontSize: '11px',
                  fontWeight: 700,
                  color: NAVY,
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                  padding: '2px 18px 2px 0',
                  verticalAlign: 'top',
                  whiteSpace: 'nowrap',
                }}
              >
                {row.label}
              </td>
              <td style={{ fontSize: '12px', color: INK, padding: '2px 0' }}>{row.value}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Spacer pushes the Submitted To/By block toward the lower third */}
      <div style={{ flex: '1 1 auto', minHeight: '36px' }} />

      {/* Submitted To / Submitted By */}
      <div style={{ display: 'flex', borderTop: `1px solid ${BORDER}`, paddingTop: '14px' }}>
        <ColumnBlock heading="Submitted To" lines={model.submittedTo} />
        <ColumnBlock heading="Submitted By" lines={model.submittedBy} />
      </div>

      {/* Confidentiality note */}
      <div
        style={{
          marginTop: '18px',
          fontSize: '9px',
          fontStyle: 'italic',
          color: MUTED,
          lineHeight: 1.4,
        }}
      >
        {model.confidentialityNote}
      </div>
    </div>
  );
}

function ColumnBlock({ heading, lines }: { heading: string; lines: string[] }) {
  return (
    <div style={{ flex: '1 1 0', paddingRight: '20px' }}>
      <div
        style={{
          fontSize: '10px',
          fontWeight: 700,
          color: NAVY,
          textTransform: 'uppercase',
          letterSpacing: '0.12em',
          marginBottom: '6px',
        }}
      >
        {heading}
      </div>
      {(lines.length ? lines : [TO_BE_COMPLETED]).map((line, i) => (
        <div
          key={i}
          style={{
            fontSize: '12px',
            color: INK,
            lineHeight: 1.4,
            fontWeight: i === 0 ? 700 : 400,
          }}
        >
          {line}
        </div>
      ))}
    </div>
  );
}

export default ExportCoverPage;
